/**
 * Fertiliser arithmetic. Mirrors HHNodeServer src/utils/fertiliser.js, which
 * is authoritative for the amount a job is made with; this copy gives the
 * plan its live figures. Both pin the same worked examples
 * (fertiliser.test.ts / fertiliser.test.js).
 */

import type { FertAnalysis, FertForm, FertiliserProduct, FertMethod, FertNutrient, FertRateUnit, NutrientMap } from '../types/fertiliser';
import type { ChemicalWithRegistration } from '../types/spray';

export const FERT_NUTRIENTS: { key: FertNutrient; label: string }[] = [
  { key: 'N', label: 'Nitrogen' }, { key: 'P', label: 'Phosphorus' }, { key: 'K', label: 'Potassium' },
  { key: 'S', label: 'Sulfur' }, { key: 'Ca', label: 'Calcium' }, { key: 'Mg', label: 'Magnesium' },
  { key: 'B', label: 'Boron' }, { key: 'Zn', label: 'Zinc' }, { key: 'Cu', label: 'Copper' },
  { key: 'Mn', label: 'Manganese' }, { key: 'Fe', label: 'Iron' }, { key: 'Mo', label: 'Molybdenum' },
];
/** Trace elements are shown in g/ha; a kg/ha figure for them reads as all zeros. */
export const TRACE = new Set<FertNutrient>(['B', 'Zn', 'Cu', 'Mn', 'Fe', 'Mo']);

export const RATE_UNITS: FertRateUnit[] = ['kg/ha', 'L/ha', 'g/tree', 'mL/tree'];
export const METHOD_LABEL: Record<FertMethod, string> = {
  broadcast: 'Spread', banded: 'Banded', fertigation: 'Fertigation', foliar: 'Foliar spray',
};
export const FORM_LABEL: Record<FertForm, string> = {
  granular: 'Granular', liquid: 'Liquid', soluble: 'Soluble powder', organic: 'Organic / manure',
};

export const round = (v: number | null | undefined, dp = 2): number | null =>
  (v == null || !Number.isFinite(v) ? null : Math.round(v * 10 ** dp) / 10 ** dp);

export const productUnit = (u: FertRateUnit): 'kg' | 'L' => (u === 'L/ha' || u === 'mL/tree' ? 'L' : 'kg');

type Missing = 'rate' | 'area' | 'trees' | 'density' | 'amount' | null;

export function perHectare(rate: number, unit: FertRateUnit, areaHa: number | null, trees: number | null) {
  const u = productUnit(unit);
  if (!Number.isFinite(rate) || rate <= 0) return { amount: null, unit: u, missing: 'rate' as Missing };
  if (unit === 'kg/ha' || unit === 'L/ha') return { amount: rate, unit: u, missing: null as Missing };
  if (!(trees && trees > 0)) return { amount: null, unit: u, missing: 'trees' as Missing };
  if (!(areaHa && areaHa > 0)) return { amount: null, unit: u, missing: 'area' as Missing };
  return { amount: (rate * trees) / 1000 / areaHa, unit: u, missing: null as Missing };
}

export function quantityFor(rate: number, unit: FertRateUnit, areaHa: number | null, trees: number | null) {
  const u = productUnit(unit);
  if (!Number.isFinite(rate) || rate <= 0) return { quantity: null, unit: u, missing: 'rate' as Missing };
  if (unit === 'g/tree' || unit === 'mL/tree') {
    if (!(trees && trees > 0)) return { quantity: null, unit: u, missing: 'trees' as Missing };
    return { quantity: round((rate * trees) / 1000, 3), unit: u, missing: null as Missing };
  }
  if (!(areaHa && areaHa > 0)) return { quantity: null, unit: u, missing: 'area' as Missing };
  return { quantity: round(rate * areaHa, 3), unit: u, missing: null as Missing };
}

export function nutrientsFromAmount(product: FertAnalysis, amountPerHa: number | null, unit: 'kg' | 'L') {
  if (amountPerHa == null) return { nutrients: {} as NutrientMap, missing: 'amount' as Missing };
  let k: number | null = 1;
  if (unit === 'L') {
    if (product.analysis_basis === 'w/v') k = null;
    else if (product.density_kg_per_l && product.density_kg_per_l > 0) k = product.density_kg_per_l;
    else return { nutrients: {} as NutrientMap, missing: 'density' as Missing };
  }
  const out: NutrientMap = {};
  for (const [n, pct] of Object.entries(product.nutrient_analysis ?? {}) as [FertNutrient, number][]) {
    if (!(pct > 0)) continue;
    out[n] = round(k === null ? (amountPerHa * pct) / 100 : (amountPerHa * k * pct) / 100, 3) ?? undefined;
  }
  return { nutrients: out, missing: null as Missing };
}

export function lineNutrients(product: FertAnalysis, line: { rate: number; rate_unit: FertRateUnit }, areaHa: number | null, trees: number | null) {
  const per = perHectare(Number(line.rate), line.rate_unit, areaHa, trees);
  if (per.missing) return { nutrients: {} as NutrientMap, missing: per.missing, per_ha: null, unit: per.unit };
  return { ...nutrientsFromAmount(product, per.amount, per.unit), per_ha: round(per.amount, 3), unit: per.unit };
}

export function addNutrients(maps: NutrientMap[]): NutrientMap {
  const out: NutrientMap = {};
  for (const m of maps) {
    for (const [k, v] of Object.entries(m) as [FertNutrient, number][]) out[k] = round((out[k] ?? 0) + v, 3) ?? undefined;
  }
  return out;
}

/** "46-0-0" style grade from N, P and K, with any others after. */
export function gradeText(a: NutrientMap): string {
  const npk = `${a.N ?? 0}-${a.P ?? 0}-${a.K ?? 0}`;
  const rest = FERT_NUTRIENTS.filter((n) => !['N', 'P', 'K'].includes(n.key) && a[n.key]).map((n) => `${n.key} ${a[n.key]}%`);
  return [npk, ...rest].join(' · ');
}

/** A nutrient amount per hectare for display: kg/ha, or g/ha for trace elements. */
export function nutrientText(n: FertNutrient, kgPerHa: number | null | undefined): string {
  if (kgPerHa == null) return '—';
  if (TRACE.has(n)) return `${round(kgPerHa * 1000, 0)} g/ha`;
  return `${round(kgPerHa, 1)} kg/ha`;
}

export const MISSING_TEXT: Record<Exclude<Missing, null>, string> = {
  rate: 'no rate',
  area: 'the planting has no area',
  trees: 'the planting has no tree count',
  density: 'the product has no density, so litres cannot be turned into nutrients',
  amount: 'no amount',
};

/**
 * A fertiliser as a mix product. It has no registration (registration_id 0
 * is sent as null), no label rates and no withholding period; the rate is
 * whatever the person sets.
 */
export function fertiliserAsMixProduct(p: FertiliserProduct): ChemicalWithRegistration {
  return {
    id: p.id,
    product_name: p.product_name,
    manufacturer: p.manufacturer,
    active_ingredients: [],
    notes: p.notes,
    is_active: p.is_active,
    created_at: p.created_at,
    updated_at: p.updated_at,
    registration_id: 0,
    rate_unit: p.fertiliser_form === 'liquid' ? 'L/ha' : 'kg/ha',
    whp_days: null,
    rei_hours: null,
  } as ChemicalWithRegistration;
}
